import usePagination from "@/hooks/pagination";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { getLogs, type LogList } from "@/api/log.service";
import { CrudPage } from "@/components/crud/CrudPage";
import { Autocomplete, IconButton, Tab, Tabs, TextField, Tooltip } from "@mui/material";
import { Visibility } from "@mui/icons-material";
import dayjs from "dayjs";
import { LogDetailsDialog } from "@/components/log/LogDetailsDialog";
import { DataTable } from "@/components/DataTable";
import { FilterModal } from "@/components/FilterModal";
import { UsoAppPanel } from "@/pages/usoApp/UsoAppPanel";

type LogFilter = {
  event_type?: string;
  level?: string;
  entity_name?: string;
  username?: string;
  date?: string;
};
const levelOptions = [
  { id: "info", label: "Info" },
  { id: "error", label: "Erro" },
];

const EventosPanel = () => {
  const { total, setTotal, page, setPage, rowsPerPage, setRowsPerPage } = usePagination();
  const [logs, setLogs] = useState<LogList[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState<LogList | null>(null);
  const [inputValues, setInputValues] = useState({ event_type: "", level: "", entity_name: "", username: "", date: "" });
  const [filters, setFilters] = useState<LogFilter>({});

  const loadLogs = async () => {
    setIsLoading(true);
    try {
      const data = await getLogs({
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        ...filters,
      });

      setTotal(data.total);

      const formatted = data.logs.map((log) => ({
        ...log,
        created_at: dayjs(log.created_at).format("DD/MM/YYYY HH:mm"),
        payload: JSON.stringify(log.payload, null, 2),
        user_company: [log.user_company ?? []].flat().join(", "),
      }));

      setLogs(formatted);
    } catch (error) {
      console.error("Failed to fetch logs:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
  }, [page, rowsPerPage, filters]);

  const applyFilters = () => {
    setFilters({
      event_type: inputValues.event_type || undefined,
      level: inputValues.level || undefined,
      entity_name: inputValues.entity_name || undefined,
      username: inputValues.username || undefined,
      date: inputValues.date || undefined,
    });
    setPage(0);
  };

  const handleClearFilters = () => {
    setInputValues({ event_type: "", level: "", entity_name: "", username: "", date: "" });
    setFilters({});
    setPage(0);
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  if (isLoading) return <div>Carregando logs...</div>;

  return (
    <>
      <FilterModal activeCount={activeFilterCount} onApply={applyFilters} onClear={handleClearFilters}>
        <TextField
          label="Data"
          type="date"
          size="small"
          fullWidth
          value={inputValues.date}
          onChange={(e) => setInputValues((prev) => ({ ...prev, date: e.target.value }))}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          label="Evento"
          size="small"
          fullWidth
          value={inputValues.event_type}
          onChange={(e) => setInputValues((prev) => ({ ...prev, event_type: e.target.value }))}
        />
        <Autocomplete
          options={levelOptions}
          size="small"
          fullWidth
          value={levelOptions.find((o) => o.id === inputValues.level) || null}
          onChange={(_, newValue) => setInputValues((prev) => ({ ...prev, level: newValue?.id ?? "" }))}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => <TextField {...params} label="Nível" variant="outlined" />}
        />
        <TextField
          label="Entidade"
          size="small"
          fullWidth
          value={inputValues.entity_name}
          onChange={(e) => setInputValues((prev) => ({ ...prev, entity_name: e.target.value }))}
        />
        <TextField
          label="Ator"
          size="small"
          fullWidth
          value={inputValues.username}
          onChange={(e) => setInputValues((prev) => ({ ...prev, username: e.target.value }))}
        />
      </FilterModal>

      <DataTable
        data={logs}
        columns={[
          { key: "created_at", label: "Data" },
          { key: "event_type", label: "Evento" },
          { key: "entity_name", label: "Entidade" },
          { key: "level", label: "Nível" },
          { key: "username", label: "Ator" },
          { key: "client_name", label: "Cliente" },
        ]}
        total={total}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        actions={(log) => (
          <Tooltip title="Visualizar detalhes">
            <IconButton onClick={() => setSelectedLog(log)}>
              <Visibility />
            </IconButton>
          </Tooltip>
        )}
      />

      <LogDetailsDialog
        open={!!selectedLog}
        log={selectedLog}
        onClose={() => setSelectedLog(null)}
      />
    </>
  );
};

type Visao = "eventos" | "uso";

// Aba "Uso do App" oculta temporariamente; volte para true para reexibi-la.
const USO_APP_HABILITADO = false;

export const LogsPage = () => {
  const [params, setParams] = useSearchParams();
  const visao: Visao = USO_APP_HABILITADO && params.get("visao") === "uso" ? "uso" : "eventos";

  return (
    <CrudPage
      title="Logs"
      subtitle={
        visao === "uso"
          ? "Busca avançada: quem usa o App do Dono, em quais módulos e em quais dias"
          : "Visualização histórica de eventos do sistema"
      }
    >
      {USO_APP_HABILITADO && (
        <Tabs
          value={visao}
          onChange={(_, v: Visao) => setParams(v === "uso" ? { visao: "uso" } : {}, { replace: true })}
          sx={{ mb: 2 }}
        >
          <Tab value="eventos" label="Eventos" />
          <Tab value="uso" label="Uso do App" />
        </Tabs>
      )}
      {visao === "uso" ? <UsoAppPanel /> : <EventosPanel />}
    </CrudPage>
  );
};
